/**
 * ============================================================================
 * useCreateGreenfieldSimulation — S19.9
 * ============================================================================
 * Crea simulaciones pro-forma "desde cero" (Greenfield) sin necesidad de un
 * embarque base en la operación. Construye un payload sintético mínimo pero
 * 100% compatible con `calcLandedCostV2` y lo persiste vía INSERT directo en
 * `simulaciones_proforma` (RLS garantiza tenant isolation).
 *
 * Filosofía:
 *   - Generar UUID falso pero estable para el ítem y el documento sintético.
 *   - Inyectar un único gasto base ($0 USD, base "Peso") como semilla — el
 *     usuario podrá editarlo o añadir más vía el ExpenseManagerSheet.
 *   - Mapear arancel% e iva% en compliance.* usando el UUID del ítem.
 *   - Bullet-proof contra calcLandedCostV2: arrays nunca undefined, trmMap
 *     siempre incluye COP=1, trmAduana siempre seteado.
 * ============================================================================
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { ProformaPayloadV3 } from "./useProformaSimulations";
import type { CostEngineInput } from "@/lib/landedCostEngine";
import type { QuoteExtraction } from "@/lib/schemas/quote";

export interface GreenfieldFormInput {
  nombre: string;
  trmProyectada: number;
  /** Moneda principal de la simulación (se usa como moneda del documento sintético). Default 'USD'. */
  moneda?: string;
}

/** Input multi-ítem usado por el flujo de extracción de PDF. */
export interface GreenfieldMultiInput {
  nombre: string;
  trmProyectada: number;
  monedaPrincipal?: string;
  items: Array<{
    sku: string;
    descripcion?: string;
    cantidad: number;
    precio_unitario: number;
    peso_bruto_kg?: number;
    hs_code?: string;
    arancelPct: number;
    ivaPct: number;
  }>;
  gastos?: Array<{
    concepto: string;
    monto: number;
    moneda: string;
    base_prorrateo: "Peso" | "Valor";
  }>;
}

/** RFC4122 v4 simple — suficiente para IDs sintéticos client-side. */
function generateUUID(): string {
  // crypto.randomUUID es estándar moderno; fallback manual para entornos viejos.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function buildGreenfieldPayload(input: GreenfieldFormInput): ProformaPayloadV3 {
  const docId = generateUUID();
  const trm = Number(input.trmProyectada) > 0 ? Number(input.trmProyectada) : 4000;
  const moneda = (input.moneda || "USD").toUpperCase();

  const trmMap: Record<string, number> = { COP: 1, USD: trm };
  if (moneda !== "USD" && moneda !== "COP") {
    trmMap[moneda] = trm;
  }

  // V3: cost_engine_input preserva estado del motor (TRM/documents/compliance);
  // los arrays planos `items` y `gastos` del payload son la fuente de verdad.
  const costEngineInput: CostEngineInput = {
    documents: [
      {
        id: docId,
        moneda,
        costo_flete: 0,
        seguro: 0,
        otros_cargos: 0,
        impuestos: 0,
      },
    ],
    items: [],
    gastos: [],
    trmMap,
    trmAduana: trm,
    compliance: {
      arancelPctByItemId: {},
      ivaPctByItemId: {},
    },
    engineVersion: "v2",
  };

  return {
    schema_version: "v3",
    embarque_base: null,
    cost_engine_input: costEngineInput,
    items: [],
    gastos: [],
    sim_params: {
      comercial: { incoterm: "FOB" },
    },
    cloned_at: new Date().toISOString(),
  };
}

/**
 * Construye el payload de simulación a partir de un set multi-ítem
 * (típicamente proveniente del extractor de PDF de cotizaciones).
 */
export function buildGreenfieldFromQuote(input: GreenfieldMultiInput): ProformaPayloadV3 {
  const docId = generateUUID();
  const trm = Number(input.trmProyectada) > 0 ? Number(input.trmProyectada) : 4000;
  const moneda = (input.monedaPrincipal || "USD").toUpperCase();

  // V3 — los ítems extraídos van al array plano de nivel superior (no al
  // cost_engine_input.items, que en V3 queda vacío). Los % arancel/IVA
  // viajan en cost_engine_input.compliance keyed por itemId.
  const itemsPlanos: ProformaPayloadV3["items"] = [];
  const arancelMap: Record<string, number> = {};
  const ivaMap: Record<string, number> = {};

  input.items.forEach((it, idx) => {
    const id = generateUUID();
    const cantidad = Number(it.cantidad) || 0;
    const precio = Number(it.precio_unitario) || 0;
    itemsPlanos.push({
      id,
      documento_id: docId,
      sku: it.sku?.trim() || `ITEM-${String(idx + 1).padStart(3, "0")}`,
      descripcion: it.descripcion || it.sku || `Ítem ${idx + 1}`,
      unidad_medida: "UND",
      precio_unitario: precio,
      cantidad,
      sub_total: cantidad * precio,
      peso_bruto_kg: Number(it.peso_bruto_kg) || 0,
      volumen_cbm: 0,
      aplica_tlc: false,
      iva_override: null,
      // hs_code: HITL — propagado desde OCR. EngineItemInput no lo declara
      // pero el resto del pipeline (RPC + UI) lo lee vía cast.
      hs_code: it.hs_code?.trim() || null,
    } as ProformaPayloadV3["items"][number]);
    arancelMap[id] = Number(it.arancelPct) || 0;
    ivaMap[id] = Number(it.ivaPct) || 19;
  });

  const gastosRaw = input.gastos && input.gastos.length > 0
    ? input.gastos
    : [{ concepto: "Flete (estimado)", monto: 0, moneda: "USD", base_prorrateo: "Peso" as const }];

  const gastosPlanos: ProformaPayloadV3["gastos"] = gastosRaw.map((g) => ({
    id: generateUUID(),
    concepto: g.concepto?.trim() || "Gasto",
    monto: Number(g.monto) || 0,
    moneda: (g.moneda || "USD").toUpperCase(),
    base_prorrateo: g.base_prorrateo === "Peso" ? "Peso" : "Valor",
    aplica_costeo_inventario: true,
  }));

  const trmMap: Record<string, number> = { COP: 1, USD: trm };
  if (moneda && moneda !== "USD" && moneda !== "COP") {
    trmMap[moneda] = trm;
  }

  const costEngineInput: CostEngineInput = {
    documents: [
      {
        id: docId,
        moneda,
        costo_flete: 0,
        seguro: 0,
        otros_cargos: 0,
        impuestos: 0,
      },
    ],
    items: [],
    gastos: [],
    trmMap,
    trmAduana: trm,
    compliance: {
      arancelPctByItemId: arancelMap,
      ivaPctByItemId: ivaMap,
    },
    engineVersion: "v2",
  };

  return {
    schema_version: "v3",
    embarque_base: null,
    cost_engine_input: costEngineInput,
    items: itemsPlanos,
    gastos: gastosPlanos,
    sim_params: {
      comercial: { incoterm: "FOB" },
    },
    cloned_at: new Date().toISOString(),
  };
}

export function useCreateGreenfieldSimulation() {
  const { orgId, user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: GreenfieldFormInput): Promise<string> => {
      if (!orgId || !user) throw new Error("Sesión inválida (sin orgId).");
      if (!input.nombre.trim()) throw new Error("Nombre de simulación requerido.");

      const payload = buildGreenfieldPayload(input);

      const { data, error } = await supabase
        .from("simulaciones_proforma")
        .insert({
          org_id: orgId,
          user_id: user.id,
          nombre: input.nombre.trim(),
          descripcion: "Simulación creada desde cero (Greenfield)",
          embarque_base_id: null,
          status: "draft",
          payload: payload as unknown as never,
        })
        .select("id")
        .single();

      if (error) throw error;
      return data.id as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proforma-simulations"] });
      queryClient.invalidateQueries({ queryKey: ["proforma-simulation"] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Quote PDF Extraction → Greenfield Simulation
// ─────────────────────────────────────────────────────────────────────────────

const QUOTES_BUCKET = "cotizaciones_temporales";

function sanitizeFileName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .slice(0, 80);
}

export interface ExtractQuoteInput {
  file: File;
  nombre?: string;
  trmProyectada?: number;
}

export interface ExtractQuoteResult {
  simulationId: string;
  extracted: QuoteExtraction;
}

/**
 * Sube el PDF a `cotizaciones_temporales`, invoca la edge function
 * `extract-quote-preview`, valida la respuesta y persiste la simulación.
 */
export function useExtractQuoteSimulation() {
  const { orgId, user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ExtractQuoteInput): Promise<ExtractQuoteResult> => {
      if (!orgId || !user) throw new Error("Sesión inválida (sin orgId).");
      if (!input.file) throw new Error("Archivo requerido.");

      // 1. Upload to ephemeral bucket
      const ts = Date.now();
      const safeName = sanitizeFileName(input.file.name || "cotizacion.pdf");
      const filePath = `${user.id}/quote_${ts}_${safeName}`;

      const { error: uploadErr } = await supabase.storage
        .from(QUOTES_BUCKET)
        .upload(filePath, input.file, {
          cacheControl: "3600",
          upsert: false,
          contentType: input.file.type || "application/pdf",
        });
      if (uploadErr) {
        throw new Error(`No se pudo subir el archivo: ${uploadErr.message}`);
      }

      // 2. Invoke edge function (sync extraction)
      const { data: fnData, error: fnErr } = await supabase.functions.invoke(
        "extract-quote-preview",
        { body: { filePath } },
      );

      if (fnErr) {
        const msg = fnErr instanceof Error ? fnErr.message : "Error invocando extractor";
        throw new Error(msg);
      }
      if (!fnData?.success || !fnData?.data) {
        const errMsg = fnData?.error || "Respuesta vacía del extractor";
        throw new Error(errMsg);
      }

      const extracted = fnData.data as QuoteExtraction;

      // 3. Build payload & insert simulation
      const nombre = (input.nombre?.trim()
        || `Cotización ${extracted.proveedor || ""} ${extracted.numero_cotizacion || ""}`.trim()
        || `Cotización ${new Date().toLocaleDateString("es-CO")}`);

      const payload = buildGreenfieldFromQuote({
        nombre,
        trmProyectada: input.trmProyectada
          ?? extracted.trm_sugerida
          ?? 4000,
        monedaPrincipal: extracted.moneda_principal,
        items: extracted.items.map((it) => ({
          sku: it.sku,
          descripcion: it.descripcion,
          cantidad: it.cantidad,
          precio_unitario: it.precio_unitario,
          peso_bruto_kg: it.peso_bruto_kg,
          hs_code: it.hs_code,
          arancelPct: it.arancel_pct ?? 0,
          ivaPct: it.iva_pct ?? 19,
        })),
        gastos: extracted.gastos.map((g) => ({
          concepto: g.concepto,
          monto: g.monto,
          moneda: g.moneda,
          base_prorrateo: g.base_prorrateo,
        })),
      });

      const { data, error } = await supabase
        .from("simulaciones_proforma")
        .insert({
          org_id: orgId,
          user_id: user.id,
          nombre,
          descripcion: `Cotización extraída por IA${extracted.proveedor ? ` — ${extracted.proveedor}` : ""}`,
          embarque_base_id: null,
          status: "draft",
          payload: payload as unknown as never,
        })
        .select("id")
        .single();

      if (error) throw error;
      return { simulationId: data.id as string, extracted };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proforma-simulations"] });
      queryClient.invalidateQueries({ queryKey: ["proforma-simulation"] });
    },
  });
}

